import { useState } from "react";
import { type Identifier, useNotify } from "ra-core";
import { Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { meetingDisplayTitle } from "./meetingDisplayTitle";
import { MeetingPermission } from "./MeetingPermission";

type TitledMeeting = Parameters<typeof meetingDisplayTitle>[0] & {
  id?: Identifier;
  restricted?: boolean;
};

/**
 * The meeting page's heading, renamable in place.
 *
 * Bill's calls all arrive as "Bill Warner's Personal Meeting Room" and renaming
 * in Zoom is painful, so the name is set here and stored in custom_title, which
 * Zoom never writes (meeting 476, LCCRM-88). Clearing it restores Zoom's name.
 *
 * `onSave` is the caller's: the review page routes it through its serialized
 * save queue so an unsaved summary edit survives, the recordings-only page
 * writes directly. It receives the trimmed name, or null to clear.
 */
export const MeetingTitle = ({
  record,
  onSave,
}: {
  record: TitledMeeting;
  onSave: (title: string | null) => Promise<void>;
}) => {
  const notify = useNotify();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const title = meetingDisplayTitle(record);
  const current = (record?.custom_title ?? "").trim() || null;

  const start = () => {
    setDraft(current ?? title);
    setEditing(true);
  };

  const submit = async () => {
    const next = draft.trim() || null;
    // Accepting the prefilled automatic name unchanged is not a rename; storing
    // it would freeze a name that is meant to follow the attendees.
    const unchanged =
      current == null ? next === title || next == null : next === current;
    if (unchanged) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      notify("Couldn't rename the meeting.", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-1 min-w-0">
        <h2 className="text-2xl font-bold tracking-tight truncate">{title}</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-muted-foreground"
          onClick={start}
          aria-label="Rename meeting"
          title="Rename meeting"
        >
          <Pencil className="size-4" />
        </Button>
        {/* Who can see it, for admins. LCCRM-93. */}
        {record?.id != null && (
          <MeetingPermission
            meeting={{ id: record.id, restricted: record.restricted }}
            className="ml-2 shrink-0"
          />
        )}
      </div>
    );
  }

  return (
    // A div, not a form: on the review page this sits inside the meeting's own
    // <form>, and a nested form is invalid HTML whose submit reaches the outer one.
    <div className="flex items-center gap-2 min-w-0">
      <Input
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setEditing(false);
          if (event.key === "Enter") {
            // Enter in an input would otherwise submit the page's form.
            event.preventDefault();
            void submit();
          }
        }}
        aria-label="Meeting name"
        placeholder="Leave empty to use Zoom's name"
        className="text-lg font-semibold h-9 w-80 max-w-full"
        disabled={saving}
      />
      <Button type="button" size="sm" onClick={submit} disabled={saving}>
        {saving ? "Saving..." : "Save"}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => setEditing(false)}
        disabled={saving}
      >
        Cancel
      </Button>
    </div>
  );
};
